import type {
  WorkspaceSpotlightTourDetail,
  WorkspaceSpotlightTourStep,
} from '../../../../shared/ui/common/WorkspaceSpotlightTour';
import type { ExportTourContentArgs } from './types';
import {
  getColumnPanelFallbackRect,
  getColumnSearchFallbackRect,
  getExportButtonFallbackRect,
  getExportSettingsButtonFallbackRect,
  getExportSettingsDialogFallbackRect,
  getFirstRowFallbackRect,
  getHeaderFallbackRect,
  getManageColumnsButtonFallbackRect,
  getOutputPathFallbackRect,
  getPickAndExportButtonFallbackRect,
  getRefreshButtonFallbackRect,
  getRowHighlightButtonFallbackRect,
  getSelectionSummaryFallbackRect,
  getSettingsGroupingFallbackRect,
  getSettingsValidationFallbackRect,
  getSystemCountFallbackRect,
  getSystemRowFallbackRect,
  getSystemTableFallbackRect,
  getSystemTableToolsFallbackRect,
  getVisualCheckButtonFallbackRect,
} from './fallbackRects';

const detail = (
  label: string,
  bullets: string[],
  tone: WorkspaceSpotlightTourDetail['tone'] = 'default'
): WorkspaceSpotlightTourDetail => ({ label, bullets, tone });

export const buildExportTourSteps = ({
  systemCount,
  refs,
}: ExportTourContentArgs): WorkspaceSpotlightTourStep[] => [
  {
    id: 'header',
    title: 'PCF export controls',
    focusLabel: 'System count, output folder, and export buttons',
    intro: 'Use this bar to prepare Piping Component File (PCF) files from the current Revit model.',
    details: [detail('What you can do here', [
      'Refresh the system list, check systems in Revit, choose table columns, change export settings, and start an export.',
      'Tour and Guide explain the page without changing the model or export settings.',
    ])],
    nextAction: 'Check that the system list is current before choosing what to export.',
    targetRef: refs.headerRef,
    fallbackRect: getHeaderFallbackRect(),
  },
  {
    id: 'refresh-systems',
    title: 'Refresh the system list',
    focusLabel: 'Refresh piping systems and system count',
    intro: 'The list shows the piping systems read from Revit. Refresh it after changing the model.',
    details: [detail('What to check', [
      'The count shows all loaded systems, or the number selected for export.',
      'When the model has changed, the count shows “refresh” and the Refresh piping systems button turns amber.',
    ])],
    nextAction: 'Select Refresh piping systems if the model has changed.',
    targetRef: refs.refreshButtonRef,
    relatedTargets: [{
      label: 'System count',
      targetRef: refs.systemCountRef,
      fallbackRect: getSystemCountFallbackRect(),
    }],
    fallbackRect: getRefreshButtonFallbackRect(),
  },
  {
    id: 'system-table-tools',
    title: 'Search, sort, and filter',
    focusLabel: 'System table column controls',
    intro: 'Use the column headings to find the systems you need before selecting rows.',
    details: [detail('How to use them', [
      'Enter text in a column search box, open its filter menu, or select the heading to change the sort order.',
      'The checkbox at the left selects every system currently shown by the filters.',
    ])],
    nextAction: 'Filter the list if you do not need to see every system.',
    targetRef: refs.systemTableToolsRef,
    fallbackRect: getSystemTableToolsFallbackRect(),
  },
  {
    id: 'systems',
    title: 'Check the systems table',
    focusLabel: `${systemCount} loaded piping system${systemCount === 1 ? '' : 's'}`,
    intro: 'Each row represents one piping system available for export from Revit.',
    details: [detail('What the columns show', [
      'Elements shows the number of Revit elements. Exports shows how many times the system was exported in this session.',
      'Status shows validation results. Model selects the system elements in Revit.',
      'Columns added through Manage columns are for checking model information and do not change the PCF file.',
    ])],
    nextAction: 'Check the element count and status, then choose the systems you need.',
    targetRef: refs.systemTableRef,
    fallbackRect: getSystemTableFallbackRect(),
  },
  {
    id: 'system-row-actions',
    title: 'Choose systems to export',
    focusLabel: 'First piping system row',
    intro: 'Select one or more rows when you want to export only part of the list.',
    details: [detail('How to use a row', [
      'Use the checkbox, select the row, or press Enter or Space to add it to the selection.',
      'Select the status to read validation details, or use Model to find the system elements in Revit.',
    ])],
    nextAction: 'Select the rows you need, or leave all rows clear to export every system.',
    keepSpotlightClear: true,
    targetRef: refs.firstSystemRowRef,
    relatedTargets: [{
      label: 'Visible system rows',
      targetRef: refs.systemTableRowsRef,
      fallbackRect: getSystemRowFallbackRect(),
      affectsCalloutLayout: false,
    }],
    fallbackRect: getFirstRowFallbackRect(),
  },
  {
    id: 'row-highlights',
    title: 'Mark rows for review',
    focusLabel: 'Row highlight color button',
    intro: 'Add a color to selected rows when you want to return to them later.',
    details: [detail('What highlights change', [
      'Highlights are saved for this Revit document, but they do not change selection, validation, or PCF output.',
      'Alt+1 to Alt+5 apply a color. Alt+0 clears it.',
    ])],
    nextAction: 'Mark any rows that need another check, then continue.',
    targetRef: refs.rowHighlightButtonRef,
    fallbackRect: getRowHighlightButtonFallbackRect(),
  },
  {
    id: 'visual-check',
    title: 'Check grouping in Revit',
    focusLabel: 'Visual check button',
    intro: 'Visual check colors piping in the active Revit 3D view using the selected export grouping.',
    details: [detail('How to use it', [
      'Choose System Name, System Type Name, or the parameter selected in Export settings.',
      'Reset visual check restores the previous view colors. It does not change the model or PCF output.',
    ])],
    nextAction: 'Use Visual check if colors will help you spot a system in the wrong group.',
    targetRef: refs.visualCheckButtonRef,
    fallbackRect: getVisualCheckButtonFallbackRect(),
  },
  {
    id: 'columns',
    title: 'Choose table columns',
    focusLabel: 'Manage columns panel',
    intro: 'Manage columns lets you show more Revit and PCF information in the systems table.',
    details: [detail('How to use it', [
      'Turn on the properties needed for this check and hide those you do not need.',
      'These columns help you review the model; they do not change export settings or PCF output.',
    ])],
    nextAction: 'Choose the information you need to compare between systems.',
    targetRef: refs.columnPanelRef,
    relatedTargets: [{
      label: 'Open manage columns panel button',
      targetRef: refs.manageColumnsButtonRef,
      fallbackRect: getManageColumnsButtonFallbackRect(),
    }],
    fallbackRect: getColumnPanelFallbackRect(),
  },
  {
    id: 'column-search',
    title: 'Find a column',
    focusLabel: 'Search columns field',
    intro: 'Search by a Revit or PCF parameter name when the column list is long.',
    allowTargetInteraction: true,
    details: [detail('How to use it', [
      'Enter part of the parameter name, then turn the matching column on or off.',
    ])],
    nextAction: 'Clear the search when you want to see the full column list again.',
    targetRef: refs.propertySearchRef,
    fallbackRect: getColumnSearchFallbackRect(),
  },
  {
    id: 'export-settings',
    title: 'Review export settings',
    focusLabel: 'Export settings dialog',
    intro: 'Export settings control how systems are grouped and whether linked models and validation are included.',
    details: [detail('What to check', [
      'Check Group systems by, Run validation before export, and Include linked models.',
      'The Output path here is the saved default. The command bar lets you change the folder for the current export.',
    ])],
    nextAction: 'Confirm these settings match the model information you need to export.',
    targetRef: refs.exportSettingsDialogRef,
    relatedTargets: [{
      label: 'Open export settings button',
      targetRef: refs.exportSettingsButtonRef,
      fallbackRect: getExportSettingsButtonFallbackRect(),
    }],
    fallbackRect: getExportSettingsDialogFallbackRect(),
  },
  {
    id: 'settings-grouping',
    title: 'Choose how files are grouped',
    focusLabel: 'Group systems by options',
    intro: 'This setting decides which Revit value is used to separate piping into PCF files.',
    details: [detail('Available choices', [
      'Choose System Name, System Type Name, or a project parameter used by your team.',
      'Use Visual check to preview the same grouping with colors in Revit.',
    ])],
    nextAction: 'Keep the current choice unless the PCF files must be separated in another way.',
    targetRef: refs.settingsGroupingRef,
    fallbackRect: getSettingsGroupingFallbackRect(),
  },
  {
    id: 'output-path',
    title: 'Choose the output folder',
    focusLabel: 'Output folder and Change folder button',
    intro: 'The output folder in the command bar is where this export will write the PCF files.',
    details: [detail('How to change it', [
      'Enter a folder path or select Change folder to choose a location.',
      'If the field is empty, PipeEX uses the default project export path.',
    ])],
    nextAction: 'Confirm the folder before starting the export.',
    targetRef: refs.outputPathRef,
    fallbackRect: getOutputPathFallbackRect(),
  },
  {
    id: 'settings-validation',
    title: 'Run validation before export',
    focusLabel: 'Run validation before export switch',
    intro: 'Turn this on to check the latest Revit model each time an export starts.',
    details: [detail('What it does', [
      'Validation reports model warnings and errors before PipeEX creates the files.',
      'It does not repair model elements. Review any reported issue and decide whether the export can continue.',
    ])],
    nextAction: 'Choose whether this export needs a new validation check.',
    targetRef: refs.settingsValidationRef,
    fallbackRect: getSettingsValidationFallbackRect(),
  },
  {
    id: 'selection-summary',
    title: 'Check the export selection',
    focusLabel: 'Selected systems and validation status',
    intro: 'This summary shows how many systems will be exported and whether they have validation warnings or errors.',
    details: [detail('How to read it', [
      'With no selected rows, Export all uses every loaded system. Selecting rows changes the count to the selected systems.',
      'The status beside the count reads Ready, needs validation, warnings, or errors.',
    ])],
    nextAction: 'Make sure the count and status match what you intend to export.',
    targetRef: refs.selectionSummaryRef,
    fallbackRect: getSelectionSummaryFallbackRect(),
  },
  {
    id: 'pick-and-export',
    title: 'Pick elements in Revit',
    focusLabel: 'Pick in Revit button',
    intro: 'Use Pick in Revit when selecting elements in the 3D view is easier than selecting systems in the table.',
    details: [detail('How to use it', [
      'Select Pick in Revit, choose the elements in Revit, and finish the Revit selection.',
      'PipeEX uses the current export settings and output folder for the picked elements.',
    ])],
    nextAction: 'Use Pick in Revit for an element selection, or continue to export from the systems table.',
    targetRef: refs.pickAndExportButtonRef,
    fallbackRect: getPickAndExportButtonFallbackRect(),
  },
  {
    id: 'export-actions',
    title: 'Start the PCF export',
    focusLabel: 'Export all or Export selected button',
    intro: 'The main export button creates PCF files for all loaded systems or for the rows you selected.',
    details: [
      detail('Before you start', [
        'Check the selected system count, validation status, grouping, linked-model setting, and output folder.',
      ], 'accent'),
      detail('While it runs', [
        'PipeEX reads the chosen Revit elements, creates the PCF files locally, and writes them to the output folder.',
        'If an export fails, include the Trace ID from the progress window when asking for help.',
      ]),
    ],
    nextAction: 'Select Export all or Export selected when these checks are complete.',
    targetRef: refs.exportButtonRef,
    fallbackRect: getExportButtonFallbackRect(),
  },
];
